/**
 * @file     filterDelta.ts
 * @purpose  Pure provenance diff: which filter fields the buyer changed since
 *           the AI last set the search. The AI's last-intended set vs the map's
 *           current set → the buyer's manual delta. Filter metadata only (no
 *           MLS rows), so it is ADR-009 safe to hand to the model.
 */

import type { TraditionalSearchValue } from '@/components/search/searchValue';

/** Fields the AI reasons about and the server can re-count. The overlap of
 *  TraditionalSearchValue and AiFilters — map-only fields (address, cross
 *  street, postal, openHouse) are excluded because the AI never owns them. */
export const PROVENANCE_FIELDS = [
  'regions',
  'neighbourhoods',
  'propertyTypes',
  'priceMin',
  'priceMax',
  'rentMin',
  'rentMax',
  'bedsMin',
  'bathsMin',
  'livingAreaMin',
  'parkingMin',
] as const;

export type ProvenanceField = (typeof PROVENANCE_FIELDS)[number];

export interface ManualEditDelta {
  fields: ProvenanceField[];
  /** The AI's last-intended value for each changed field. */
  from: Partial<TraditionalSearchValue>;
  /** The buyer's current value for each changed field. */
  to: Partial<TraditionalSearchValue>;
}

/** Order-insensitive equality for arrays; nullish-normalized for scalars. */
function fieldEqual(a: unknown, b: unknown): boolean {
  if (Array.isArray(a) || Array.isArray(b)) {
    const aa = Array.isArray(a) ? a : [];
    const bb = Array.isArray(b) ? b : [];
    if (aa.length !== bb.length) return false;
    const set = new Set(bb.map(String));
    return aa.every((x) => set.has(String(x)));
  }
  return (a ?? null) === (b ?? null);
}

/** The fields where the buyer's `current` search differs from what the AI last
 *  applied (`prior`). Null when nothing changed. */
export function computeManualEdits(
  prior: Partial<TraditionalSearchValue>,
  current: Partial<TraditionalSearchValue>,
): ManualEditDelta | null {
  const fields: ProvenanceField[] = [];
  const from: Record<string, unknown> = {};
  const to: Record<string, unknown> = {};
  for (const key of PROVENANCE_FIELDS) {
    if (!fieldEqual(prior[key], current[key])) {
      fields.push(key);
      from[key] = prior[key];
      to[key] = current[key];
    }
  }
  return fields.length > 0 ? { fields, from, to } : null;
}
